// frontend/src/pages/UserDashboard.js

import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Avatar,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  useTheme,
  alpha
} from '@mui/material';
import {
  Person,
  Schedule,
  ArrowForward,
  Dashboard as DashboardIcon,
  Factory,
  Build,
  Inventory,
  Assignment,
  ShoppingCart,
  Settings,
  BarChart,
  CloudUpload,
  ViewList,
  AccountCircle
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';

// Map page keys to icons and display info
const PAGE_INFO = {
  'dashboard': { icon: <DashboardIcon />, label: 'Dashboard', path: '/dashboard', color: 'primary' },
  'manufacturing': { icon: <Factory />, label: 'Manufacturing', path: '/manufacturing', color: 'secondary' },
  'shop-floor': { icon: <Build />, label: 'Shop Floor', path: '/shop-floor', color: 'success' },
  'inventory': { icon: <Inventory />, label: 'Inventory Management', path: '/manufacturing/inventory', color: 'warning' },
  'work-orders': { icon: <Assignment />, label: 'Work Orders', path: '/manufacturing/work-orders', color: 'info' },
  'customer-orders': { icon: <ShoppingCart />, label: 'Customer Orders', path: '/manufacturing/customer-orders', color: 'error' },
  'bom': { icon: <ViewList />, label: 'Bill of Materials', path: '/manufacturing/bom', color: 'primary' },
  'items': { icon: <ViewList />, label: 'Items Management', path: '/manufacturing/items', color: 'secondary' },
  'operations': { icon: <Build />, label: 'Operations', path: '/manufacturing/operations', color: 'success' },
  'import': { icon: <CloudUpload />, label: 'Data Import', path: '/import', color: 'warning' },
  'settings': { icon: <Settings />, label: 'Settings', path: '/settings', color: 'info' },
  'analytics': { icon: <BarChart />, label: 'Analytics', path: '/analytics', color: 'error' },
  'reports': { icon: <BarChart />, label: 'Reports', path: '/reports', color: 'primary' },
  'tasks': { icon: <Assignment />, label: 'Tasks', path: '/tasks', color: 'secondary' },
  'profile': { icon: <AccountCircle />, label: 'My Profile', path: '/profile', color: 'success' },
};

const UserDashboard = () => {
  const { user, allowedPages } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [currentTime, setCurrentTime] = useState(new Date());

  usePageTitle('User Dashboard');

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const formatTime = () => {
    return currentTime.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = () => {
    return currentTime.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get accessible pages with info
  const accessiblePages = allowedPages
    .filter(pageKey => PAGE_INFO[pageKey] && pageKey !== 'dashboard') // Don't show dashboard in the list
    .map(pageKey => ({
      key: pageKey,
      ...PAGE_INFO[pageKey]
    }));

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Welcome Header */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 4, 
          mb: 4, 
          borderRadius: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar 
              sx={{ 
                width: 80, 
                height: 80, 
                bgcolor: alpha(theme.palette.common.white, 0.2),
                fontSize: '2rem',
                fontWeight: 600,
                border: `3px solid ${alpha(theme.palette.common.white, 0.3)}`
              }}
            >
              {user?.username?.[0]?.toUpperCase() || 'U'}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography variant="h3" gutterBottom fontWeight={700}>
              {getGreeting()}, {user?.first_name || user?.username}!
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              {formatDate()}
            </Typography>
          </Grid>
          <Grid item>
            <Box sx={{ textAlign: 'right' }}>
              <Schedule sx={{ fontSize: 48, opacity: 0.8, mb: 1 }} />
              <Typography variant="h4" fontWeight={600}>
                {formatTime()}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: alpha(theme.palette.common.white, 0.1),
            zIndex: 0
          }}
        />
      </Paper>

      <Grid container spacing={3}>
        {/* User Info Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Person sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  Your Information
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Username
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {user?.username}
                </Typography>
              </Box>

              {user?.email && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Email
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {user.email}
                  </Typography>
                </Box>
              )}

              {(user?.first_name || user?.last_name) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Full Name
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {user.first_name} {user.last_name}
                  </Typography>
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Role
                </Typography>
                <Chip 
                  label={user?.role || 'User'} 
                  color="primary" 
                  size="small" 
                />
              </Box>

              {user?.department && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Department
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {user.department}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Button
                fullWidth
                variant="outlined"
                startIcon={<Person />}
                onClick={() => navigate('/profile')}
              >
                View Full Profile
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Access Pages */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Your Accessible Pages
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                You have access to {accessiblePages.length} page{accessiblePages.length !== 1 ? 's' : ''}
              </Typography>

              {accessiblePages.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 4,
                  color: 'text.secondary' 
                }}>
                  <Typography variant="body1">
                    No additional pages available. Contact your administrator for access.
                  </Typography>
                </Box>
              ) : (
                <List sx={{ pt: 0 }}>
                  {accessiblePages.map((page) => (
                    <ListItem 
                      key={page.key} 
                      disablePadding 
                      sx={{ mb: 1 }}
                    >
                      <ListItemButton
                        onClick={() => navigate(page.path)}
                        sx={{
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.05)
                          }
                        }}
                      >
                        <ListItemIcon>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: alpha(theme.palette[page.color].main, 0.1),
                              color: `${page.color}.main`
                            }}
                          >
                            {page.icon}
                          </Box>
                        </ListItemIcon>
                        <ListItemText 
                          primary={page.label}
                          primaryTypographyProps={{
                            fontWeight: 600
                          }}
                        />
                        <ArrowForward sx={{ color: 'text.secondary' }} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Welcome Message */}
        <Grid item xs={12}>
          <Paper 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`
            }}
          >
            <Factory sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Welcome to Gopak Manufacturing Control
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Access your assigned pages using the quick links above. 
              {accessiblePages.length > 0 && ' Click on any page to get started.'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default UserDashboard;